import { Label } from '@/components/ui/label'
import CustomDrawerModal from './CustomDrawerModal'
import { DialogFooter } from '../ui/dialog'
import ActionButton from './ActionButton'
import { CommonModalType } from '@/utils/interfaces'
import { useSelector } from '@/lib/store'
import { useEffect, useState } from 'react'
import useCustomToast from '@/hooks/useCustomToast'
import {
  calculateAge,
  deepClone,
  getAllPrimaryProfiles,
  isEmpty,
} from '@/utils/commonFunc'
import PATIENT_HELPER from '@/helper/patientHelper'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { PATIENT_FAMILY_UPDATE } from '@/helper/apiConstants'
import { consumerRequest } from '@/helper/request'
import { loadState } from '@/utils/localStorage'

interface SelectMultipleCommonModalType extends CommonModalType {
  mobile: string | null | undefined
  alreadyRegisteredMembers: any
  mappingSuccessHandler: () => void
}

const SelectMultipleMemberModal = ({
  open,
  onOpenChange,
  mobile,
  alreadyRegisteredMembers = [],
  mappingSuccessHandler,
}: SelectMultipleCommonModalType) => {
  const showToast = useCustomToast()
  const loggedInMobileNumber = loadState('ctxVal') || null

  const [familyMemebers, setFamilyMembers] = useState([])
  const { handleGetUserDetails } = PATIENT_HELPER()

  const userState = useSelector((state) => state.user)

  const [selectedUsers, setSelectedUsers] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isEmpty(alreadyRegisteredMembers))
      setFamilyMembers(alreadyRegisteredMembers)
  }, [alreadyRegisteredMembers])

  const handleMapping = async () => {
    if (isEmpty(selectedUsers)) {
      showToast('danger', 'Please select at least one profile to map')
      return
    }
    const isRelationNotSelected = selectedUsers?.find((item: any) =>
      isEmpty(item?.relation)
    )
    if (isRelationNotSelected) {
      showToast('danger', 'Please choose relation for all selected members')
      return
    }
    setLoading(true)

    const primaryProfile = getAllPrimaryProfiles(userState?.membersList)?.[0]
    const formData = {
      loggedInUserPhoneNumber: loggedInMobileNumber,
      primaryMemberPatientCode: primaryProfile?.code,
      familyMemberList: selectedUsers?.map((singleItem: any) => ({
        patientCode: singleItem?.code,
        relation: singleItem?.relation,
      })),
    }

    const apiRes: any = await consumerRequest(
      PATIENT_FAMILY_UPDATE,
      'post',
      formData
    )
    if (apiRes?.status === true) {
      await handleGetUserDetails()
      showToast('success', apiRes?.message)
      mappingSuccessHandler()
    } else {
      showToast('danger', apiRes?.message)
    }
    setLoading(false)
  }

const handleInputChange = (fieldName: string, value: any, index: number) => {
  const families = deepClone(familyMemebers)
  families[index][fieldName] = value
  setFamilyMembers(families)
  const alreadySelectedIndex = selectedUsers?.findIndex(
    (item) => item?.code == families?.[index]?.code
  )

  if (alreadySelectedIndex > -1) {
    const selectedM = deepClone(selectedUsers)
    selectedM[alreadySelectedIndex][fieldName] = value
    setSelectedUsers(selectedM)
  }
}

  return (
    <CustomDrawerModal
      open={open}
      title={'Registered members found'}
      dialogClassName="p-0"
      onOpenChange={onOpenChange}
      outsideClickDisabled={true}
      dialogContentClassName={''}
    >
      <div className="p-6 pt-4 pb-0">
        <p className="text-label-sm text-gray-700 text-center mb-4">
          {`Below profiles found against mobile number ${mobile}. Please select to add to your members.`}
        </p>
        <div className="radio">
          <div className="rounded-lg max-h-[256px] overflow-y-auto bg-gray-50 divide-y divide-gray-200 border border-solid border-gray-200">
            {!isEmpty(familyMemebers) &&
              familyMemebers.map((user: any, index: number) => {
                const isChecked = selectedUsers.some(
                  (u) => u.code === user.code
                )
                return (
                  <Label
                    htmlFor={user.code}
                    className="cursor-pointer"
                    key={user?.code}
                  >
                    <div className="p-4 border-b border-solid border-gray-200">
                      <div className="flex items-start justify-between space-x-2 mb-4">
                        <div>
                          <p className="text-label-lg font-semibold mb-1 cursor-pointer">
                            {user.firstName} {user?.lastName}
                          </p>
                          <p className="text-body-sm text-gray-700 font-medium capitalize">
                            {user?.age ? user?.age : calculateAge(user?.dob)} Yr
                            / {String(user?.gender).toLowerCase()} |{' '}
                            {user?.phoneNumber}
                          </p>
                        </div>
                        <Checkbox
                          id={user.code}
                          checked={isChecked}
                          onCheckedChange={(checked) => {
                            setSelectedUsers((prev) =>
                              checked
                                ? [...prev, user]
                                : prev.filter((u) => u.code !== user.code)
                            )
                          }}
                        />
                      </div>
                      {isChecked && (
                        <div className="">
                          <Label
                            className="text-label-xs text-gray-700 mb-2 block"
                            htmlFor="relation"
                          >
                            Select relation*
                          </Label>
                          <Select
                            // {...register('relation')}
                            value={user?.relation}
                            onValueChange={(value) => {
                              handleInputChange('relation', value, index)
                            }}
                          >
                            <SelectTrigger className="bg-white">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent id="relation">
                              <SelectItem value="FATHER">Father</SelectItem>
                              <SelectItem value="MOTHER">Mother</SelectItem>
                              <SelectItem value="SISTER">Sister</SelectItem>
                              <SelectItem value="BROTHER">Brother</SelectItem>
                              <SelectItem value="SPOUSE">Spouse</SelectItem>
                              <SelectItem value="SON">Son</SelectItem>
                              <SelectItem value="DAUGHTER">Daughter</SelectItem>
                              <SelectItem value="OTHER">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </Label>
                )
              })}
          </div>
        </div>
      </div>

      <hr className="border-gray-100" />
      <DialogFooter className="pt-4 p-6 ">
        <ActionButton
          variant="primary"
          title="Proceed and create"
          loading={loading}
          loadingText="Creating"
          disabled={loading}
          className="text-label-lg w-full"
          onClick={handleMapping}
        />
      </DialogFooter>
    </CustomDrawerModal>
  )
}

export default SelectMultipleMemberModal
